/**
 * product-suggestions-tabs.js
 *
 * Onglets de la section « Product suggestions tabs ».
 * Desktop : une barre d'onglets, un seul panneau visible a la fois.
 * Mobile : pas d'onglets, les panneaux s'empilent avec leur propre titre (gere en CSS).
 *
 * Les deux panneaux se remplissent en asynchrone (product-recommendations.js et
 * recently-viewed-products-section.js). On observe leurs mutations pour masquer
 * l'onglet d'un panneau reste vide, et la section entiere si les deux le sont.
 */

class ProductSuggestionsTabs extends HTMLElement {
  /** @type {HTMLElement[]} */
  #tabs = [];

  /** @type {HTMLElement[]} */
  #panels = [];

  /**
   * Titres mobiles, rendus par le bloc titres hors des panneaux : ils doivent
   * suivre la disponibilite de leur panneau, sinon un titre reste seul sur mobile.
   * @type {HTMLElement[]}
   */
  #titles = [];

  /** @type {MutationObserver | null} */
  #observer = null;

  connectedCallback() {
    this.#tabs = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-suggestions-tab]')]);
    this.#panels = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-suggestions-panel]')]);
    this.#titles = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-suggestions-title]')]);

    if (this.#panels.length === 0) return;

    for (const tab of this.#tabs) {
      tab.addEventListener('click', this.#handleTabClick);
      tab.addEventListener('keydown', this.#handleTabKeydown);
    }

    this.#observer = new MutationObserver(this.#refresh);
    this.#refresh();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();

    for (const tab of this.#tabs) {
      tab.removeEventListener('click', this.#handleTabClick);
      tab.removeEventListener('keydown', this.#handleTabKeydown);
    }
  }

  /** @param {Event} event */
  #handleTabClick = (event) => {
    const tab = event.currentTarget;
    if (!(tab instanceof HTMLElement) || !tab.dataset.suggestionsTab) return;

    this.#select(tab.dataset.suggestionsTab);
  };

  /** @param {KeyboardEvent} event */
  #handleTabKeydown = (event) => {
    const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (step === 0) return;

    const visibleTabs = this.#tabs.filter((tab) => !tab.hidden);
    if (visibleTabs.length < 2) return;

    event.preventDefault();

    const currentIndex = visibleTabs.findIndex((tab) => tab.dataset.suggestionsTab === this.dataset.activePanel);

    // `findIndex` rend -1 si le panneau actif n'a pas d'onglet visible. `#select` maintient
    // l'inverse, mais `data-active-panel` est ecrit sur l'hote, donc modifiable de l'exterieur.
    // Sans cette garde, le -1 partirait dans le modulo et les deux fleches renverraient
    // sur le meme onglet au lieu d'aller en sens opposes.
    if (currentIndex === -1) return;

    const nextTab = visibleTabs[(currentIndex + step + visibleTabs.length) % visibleTabs.length];

    if (nextTab?.dataset.suggestionsTab) {
      this.#select(nextTab.dataset.suggestionsTab, { focus: true });
    }
  };

  /**
   * Un panneau est disponible tant qu'il n'a pas signale qu'il etait vide.
   * Les recommandations sont considerees disponibles pendant leur chargement
   * (le squelette occupe deja la place), les produits vus recemment seulement
   * une fois les cartes inserees.
   *
   * @param {HTMLElement} panel
   * @returns {boolean}
   */
  #isAvailable(panel) {
    const recommendations = panel.querySelector('product-recommendations');
    if (recommendations instanceof HTMLElement) {
      if (recommendations.classList.contains('hidden')) return false;

      const list = recommendations.querySelector('[data-has-recommendations]');
      return !(list instanceof HTMLElement) || list.dataset.hasRecommendations !== 'false';
    }

    const recentlyViewed = panel.querySelector('recently-viewed-products-section');
    if (recentlyViewed instanceof HTMLElement) {
      // `data-suggestions-preview` : rendu par la section dans l'editeur uniquement.
      return recentlyViewed.dataset.hasProducts === 'true' || 'suggestionsPreview' in recentlyViewed.dataset;
    }

    return true;
  }

  /**
   * Recalcule la disponibilite de chaque panneau.
   * L'observer est coupe pendant l'operation : les classes qu'on pose ici sont
   * dans le sous-arbre observe et relanceraient le callback en boucle.
   */
  #refresh = () => {
    this.#observer?.disconnect();

    /** @type {string[]} */
    const availableNames = [];

    for (const panel of this.#panels) {
      const name = panel.dataset.suggestionsPanel;
      const isAvailable = this.#isAvailable(panel);

      panel.classList.toggle('is-unavailable', !isAvailable);

      if (isAvailable && name) availableNames.push(name);
    }

    for (const title of this.#titles) {
      const name = title.dataset.suggestionsTitle;
      title.classList.toggle('is-unavailable', !name || !availableNames.includes(name));
    }

    for (const tab of this.#tabs) {
      tab.hidden = !tab.dataset.suggestionsTab || !availableNames.includes(tab.dataset.suggestionsTab);
    }

    this.hidden = availableNames.length === 0;

    // L'onglet actif vient de disparaitre : on bascule sur le premier encore debout.
    if (availableNames.length > 0 && !availableNames.includes(this.dataset.activePanel ?? '')) {
      this.#select(availableNames[0]);
    }

    this.#observer?.observe(this, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class', 'data-has-products', 'data-has-recommendations'],
    });
  };

  /**
   * Les slides d'un `slideshow-component` initialise dans un panneau masque restent
   * toutes en `aria-hidden="true"`, donc en `content-visibility: hidden` : la carte
   * est vide et ne s'affiche qu'au survol.
   *
   * En cause, une course dans slideshow.js : sans geometrie, `#updateVisibleSlides()`
   * renvoie 0 et programme une ecriture qui masque toutes les slides ; le `select(0)`
   * de repli ecrit avant cette ecriture differee et se fait donc ecraser. Le
   * `ResizeObserver` ne rattrape rien, il ne recalcule que si plus d'une slide etait
   * visible au demarrage.
   *
   * On rebranche donc le composant une fois le panneau visible : sa deconnexion puis
   * sa reconnexion relancent l'initialisation avec de vraies dimensions.
   *
   * Mais on laisse d'abord passer deux frames. Un carrousel qui affichait plusieurs
   * slides se repare tout seul : son `ResizeObserver` se redeclenche quand le panneau
   * retrouve une taille, et lui recalcule bien (la garde `visibleSlidesAmount > 1`
   * est satisfaite). Le rebrancher malgre tout reinitialiserait sa position de
   * defilement pour rien. Seuls les composants encore bloques apres coup sont repris.
   *
   * @param {HTMLElement} panel
   */
  #reviveSlideshows(panel) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Le panneau a pu etre reduit entre-temps : rien a mesurer, on abandonne.
        if (panel.classList.contains('is-active')) this.#reconnectStaleSlideshows(panel);
      });
    });
  }

  /** @param {HTMLElement} panel */
  #reconnectStaleSlideshows(panel) {
    const slideshows = /** @type {HTMLElement[]} */ ([...panel.querySelectorAll('slideshow-component')]);

    const stale = slideshows.filter((slideshow) => {
      const track = slideshow.querySelector(':scope > slideshow-container > slideshow-slides');
      if (!(track instanceof HTMLElement)) return false;

      const slides = [...track.children].filter((slide) => slide.tagName === 'SLIDESHOW-SLIDE');
      return slides.length > 0 && !slides.some((slide) => slide.getAttribute('aria-hidden') === 'false');
    });

    // Rebrancher un parent rebranche ses descendants : on ne garde que les plus externes.
    for (const slideshow of stale) {
      if (stale.some((other) => other !== slideshow && other.contains(slideshow))) continue;

      const parent = slideshow.parentNode;
      if (!parent) continue;

      const next = slideshow.nextSibling;
      slideshow.remove();
      parent.insertBefore(slideshow, next);
    }
  }

  /**
   * @param {string} name
   * @param {object} [options]
   * @param {boolean} [options.focus] - Deplace le focus sur l'onglet (navigation clavier).
   */
  #select(name, options = {}) {
    this.dataset.activePanel = name;

    for (const tab of this.#tabs) {
      const isActive = tab.dataset.suggestionsTab === name;

      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');

      if (isActive && options.focus) tab.focus();
    }

    for (const panel of this.#panels) {
      const isActive = panel.dataset.suggestionsPanel === name;
      panel.classList.toggle('is-active', isActive);

      // Apres le toggle : le panneau doit etre visible pour etre mesurable.
      if (isActive) this.#reviveSlideshows(panel);
    }
  }
}

if (!customElements.get('product-suggestions-tabs')) {
  customElements.define('product-suggestions-tabs', ProductSuggestionsTabs);
}
